/**
 * Transition Detection - Human Factors Analysis
 *
 * Why time-based, not distance? Humans take 0.3-0.7s to switch between
 * throttle/brake regardless of speed (muscle memory, not distance perception).
 */

import {
  SIGNIFICANT_ACCELERATION_MS2,
  SIGNIFICANT_BRAKING_MS2,
} from './constants'
import { type CoastingPeriod } from './types'

/**
 * Human factors thresholds for transition timing.
 * Professional: <500ms | Amateur: 500-700ms | Lazy: >700ms
 */
export const TRANSITION_TIMING = {
  SEARCH_WINDOW_MS: 800,
  MAX_RAPID_TRANSITION_MS: 700,
  PROFESSIONAL_MS: 500,
  AMATEUR_ACCEPTABLE_MS: 700,
  LAZY_THRESHOLD_MS: 800,
} as const

export interface TransitionTiming {
  timeBetweenPeaksMs: number
  accelerationPeakTimeMs: number
  brakingPeakTimeMs: number
  maxAcceleration: number
  minBraking: number
}

export type TransitionSpeed = 'professional' | 'amateur' | 'lazy' | 'none'

export interface TransitionAnalysis {
  isRapid: boolean
  speed: TransitionSpeed
  timing?: TransitionTiming
}

const findTimeWindow = (
  currentIndex: number,
  allPeriods: CoastingPeriod[]
): { windowStart: number; windowEnd: number } => {
  const currentTime =
    allPeriods[currentIndex].timeBasedLocation.gpsPoint.elapsedMs
  let windowStart = currentIndex
  let windowEnd = currentIndex

  while (
    windowStart > 0 &&
    currentTime -
      allPeriods[windowStart - 1].timeBasedLocation.gpsPoint.elapsedMs <=
      TRANSITION_TIMING.SEARCH_WINDOW_MS
  ) {
    windowStart--
  }

  while (
    windowEnd < allPeriods.length - 1 &&
    allPeriods[windowEnd + 1].timeBasedLocation.gpsPoint.elapsedMs -
      currentTime <=
      TRANSITION_TIMING.SEARCH_WINDOW_MS
  ) {
    windowEnd++
  }

  return { windowStart, windowEnd }
}

/**
 * Find acceleration/braking peaks in time window.
 * CRITICAL: Uses RAW acceleration! Smoothing destroys transitions (+5 → -8 becomes ~0).
 */
const findAccelerationPeaks = (
  windowStart: number,
  windowEnd: number,
  allPeriods: CoastingPeriod[]
): {
  maxAcceleration: number
  minBraking: number
  maxAccelTime: number
  minBrakingTime: number
} => {
  let maxAcceleration = -Infinity
  let minBraking = Infinity
  let maxAccelTime = 0
  let minBrakingTime = 0

  for (let i = windowStart; i <= windowEnd; i++) {
    const accel = allPeriods[i].acceleration // RAW acceleration
    const time = allPeriods[i].timeBasedLocation.gpsPoint.elapsedMs

    if (accel > maxAcceleration) {
      maxAcceleration = accel
      maxAccelTime = time
    }
    if (accel < minBraking) {
      minBraking = accel
      minBrakingTime = time
    }
  }

  return { maxAcceleration, minBraking, maxAccelTime, minBrakingTime }
}

const classifyTransitionSpeed = (
  timeBetweenPeaksMs: number
): TransitionSpeed => {
  if (timeBetweenPeaksMs <= TRANSITION_TIMING.PROFESSIONAL_MS) {
    return 'professional'
  }
  if (timeBetweenPeaksMs <= TRANSITION_TIMING.AMATEUR_ACCEPTABLE_MS) {
    return 'amateur'
  }
  if (timeBetweenPeaksMs <= TRANSITION_TIMING.LAZY_THRESHOLD_MS) {
    return 'lazy'
  }
  return 'none'
}

/**
 * Detect rapid transitions (throttle↔brake).
 * Time-based: 100km/h→0.5s=14m, 200km/h→0.5s=28m, 300km/h→0.5s=42m
 */
export const isRapidTransition = (
  currentIndex: number,
  allPeriods: CoastingPeriod[]
): boolean => {
  const analysis = analyzeTransition(currentIndex, allPeriods)
  return analysis.isRapid
}

export const analyzeTransition = (
  currentIndex: number,
  allPeriods: CoastingPeriod[]
): TransitionAnalysis => {
  const { windowStart, windowEnd } = findTimeWindow(currentIndex, allPeriods)

  const { maxAcceleration, minBraking, maxAccelTime, minBrakingTime } =
    findAccelerationPeaks(windowStart, windowEnd, allPeriods)

  const hasSignificantAccel = maxAcceleration >= SIGNIFICANT_ACCELERATION_MS2
  const hasSignificantBraking = minBraking <= SIGNIFICANT_BRAKING_MS2

  if (!hasSignificantAccel || !hasSignificantBraking) {
    return {
      isRapid: false,
      speed: 'none',
    }
  }

  const timeBetweenPeaksMs = Math.abs(maxAccelTime - minBrakingTime)
  const speed = classifyTransitionSpeed(timeBetweenPeaksMs)
  const isRapid =
    timeBetweenPeaksMs <= TRANSITION_TIMING.MAX_RAPID_TRANSITION_MS

  return {
    isRapid,
    speed,
    timing: {
      timeBetweenPeaksMs,
      accelerationPeakTimeMs: maxAccelTime,
      brakingPeakTimeMs: minBrakingTime,
      maxAcceleration,
      minBraking,
    },
  }
}

/**
 * Influence zone configuration for transitions.
 *
 * When a transition is detected, surrounding points are affected to prevent
 * visual spikes: 0% → 75% → 0%
 */
export const TRANSITION_INFLUENCE = {
  /** At transition point: 0% coasting */
  AT_TRANSITION_CAP: 0,

  /** ±1 point from transition: cap at 20% */
  ADJACENT_POINT_CAP: 0.2,

  /** ±2 points from transition: cap at 45% */
  NEAR_POINT_CAP: 0.45,

  /** Maximum distance (in points) where influence applies */
  MAX_INFLUENCE_DISTANCE: 2,
} as const

/** Apply transition influence to cap coasting near rapid transitions. */
export const applyTransitionInfluence = (
  periods: CoastingPeriod[]
): CoastingPeriod[] => {
  if (periods.length < 3) return periods

  const result = [...periods]

  const transitionPoints: number[] = []
  for (let i = 0; i < result.length; i++) {
    if (isRapidTransition(i, result)) {
      transitionPoints.push(i)
    }
  }

  for (let i = 0; i < result.length; i++) {
    let minDistance = Infinity
    for (const transIdx of transitionPoints) {
      const distance = Math.abs(i - transIdx)
      minDistance = Math.min(minDistance, distance)
    }

    if (minDistance <= TRANSITION_INFLUENCE.MAX_INFLUENCE_DISTANCE) {
      const currentIntensity = result[i].coastingIntensity
      let cappedIntensity = currentIntensity

      if (minDistance === 0) {
        cappedIntensity = TRANSITION_INFLUENCE.AT_TRANSITION_CAP
      } else if (minDistance === 1) {
        cappedIntensity = Math.min(
          currentIntensity,
          TRANSITION_INFLUENCE.ADJACENT_POINT_CAP
        )
      } else if (minDistance === 2) {
        cappedIntensity = Math.min(
          currentIntensity,
          TRANSITION_INFLUENCE.NEAR_POINT_CAP
        )
      }

      result[i] = {
        ...result[i],
        coastingIntensity: cappedIntensity,
      }
    }
  }

  return result
}
